import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import type {Component, Editor} from 'grapesjs';
import {useIsSmallerThanBreakpointWidth} from '@Hooks/useIsSmallerThanBreakpoint';
import {ScreenBreakpoints} from '@Libraries/responsiveness-library';
import {GA4EventName, trackGA4CustomEvent} from '@Libraries/ga-events';
import {isUserLoggedIn} from '@Libraries/user.library';
import type {
  EmailMakerActions,
  EmailMakerCanvasDevice,
  EmailMakerBrandKitSuggestionsTarget,
  EmailMakerColorPickerTarget,
  EmailMakerContextProps,
  EmailMakerImmutableStore,
  EmailMakerSaveErrorState,
  EmailMakerState,
} from '@EmailMakerReact/email-maker-react.types';
import {EditorViewport, EMAIL_MAKER_INITIAL_STATE, EmailMakerSaveState, RightPanelView} from '@EmailMakerReact/email-maker-react.types';
import type {EmailMakerBannerOptions, EmailMakerPluginDeps} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/email-maker-plugin.types';
import {installEmailMakerPlugin} from '@EmailMakerReact/grapesjs-email-maker-plugin/src';
import {EMAIL_MAKER_BANNER_EVENT} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/commands';
import {
  canRedo,
  canUndo,
  redo as canvasRedo,
  selectComponent as canvasSelectComponent,
  setCanvasDevice as canvasSetDevice,
  undo as canvasUndo,
} from '@EmailMakerReact/lib/email-maker-canvas-library';
import {destroyEditor, mountEditor, waitForEditorLoad} from '@EmailMakerReact/lib/email-maker-library';
import {isAiEditEnabled} from '@EmailMakerReact/lib/email-maker-ai-library';
import {getInlinedHtml} from '@EmailMakerReact/lib/email-maker-html-export-library';
import {seedEmailMakerNamespace} from '@EmailMakerReact/lib/email-maker-namespace-library';
import {authenticateUser} from '@EmailMakerReact/lib/email-maker-publish-library';
import {
  EmailMakerConcurrentEditError,
  EmailMakerSaveError,
  EmailMakerSaveErrorCode,
  isEmailMakerAutosaveEnabled,
  isEmailMakerSaveInFlight,
  saveEmailTemplateToCache,
} from '@EmailMakerReact/lib/email-maker-storage-library';
import {useEmailMakerLifecycleGuards} from '@EmailMakerReact/hooks/use-email-maker-lifecycle-guards';

export interface UseEmailMakerContextStateArgs {
  store: EmailMakerImmutableStore;
  canvasContainer: HTMLElement | null;
}

declare global {
  interface Window {
    /** Debugging/puppeteer escape hatch. */
    pmwEmailMakerEditor?: Editor;
  }
}

/**
 * Mounts the GrapesJS editor with the full email maker plugin and owns the React-facing editor state.
 * GrapesJS autoloads the template via the storage adapter (do not call editor.load() — it double-fetches).
 * GrapesJS autosaves on change:changesCount; the storage library absorbs save storms.
 */
export function useEmailMakerContextState(args: UseEmailMakerContextStateArgs): EmailMakerContextProps {
  const [editor, setEditor] = useState<Editor | null>(null);
  const [state, setState] = useState<EmailMakerState>(EMAIL_MAKER_INITIAL_STATE);

  const isMobile = useIsSmallerThanBreakpointWidth(ScreenBreakpoints.SMALL);
  const isTablet = useIsSmallerThanBreakpointWidth(ScreenBreakpoints.LARGE);
  const editorViewport = resolveEditorViewport(isMobile, isTablet);
  useEffect(() => {
    setState((s) => (s.editorViewport === editorViewport ? s : {...s, editorViewport}));
  }, [editorViewport]);

  const editorRef = useRef<Editor | null>(null);

  useEffect(() => {
    if (!args.canvasContainer) {
      return undefined;
    }
    if (editorRef.current) {
      return undefined;
    }

    const {editor: e, teardownPlugin, teardownNamespace} = createEmailMakerEditor(args.canvasContainer, args.store);
    editorRef.current = e;
    setEditor(e);
    wireEditorListeners(e, setState);
    exposeEditorGlobal(e);

    waitForEditorLoad(e)
      .then(() => {
        if (editorRef.current !== e) {
          return;
        }
        finishEditorLoad(e, setState);
      })
      .catch((err: unknown) => {
        console.error('Email maker post-load bootstrap failed', err);
      });

    return (): void => {
      teardownPlugin?.();
      destroyEditor(e);
      editorRef.current = null;
      setEditor(null);
      clearEditorGlobal();
      teardownNamespace();
    };
  }, [args.canvasContainer, args.store]);

  useEmailMakerLifecycleGuards(editor);

  const save = useCallback(async (): Promise<boolean> => {
    if (!editor) {
      return false;
    }
    return (await saveEmailTemplateToCache(editor)) !== null;
  }, [editor]);

  const actions: EmailMakerActions = useMemo(
    () => ({
      save,
      undo: (): void => {
        if (editor && canUndo(editor)) {
          canvasUndo(editor);
        }
      },
      redo: (): void => {
        if (editor && canRedo(editor)) {
          canvasRedo(editor);
        }
      },
      setCanvasDevice: (device: EmailMakerCanvasDevice): void => {
        setState((s) => ({...s, canvasDevice: device}));
        if (editor) {
          canvasSetDevice(editor, device);
        }
      },
      selectComponent: (component: Component | null): void => {
        if (editor) {
          canvasSelectComponent(editor, component);
        }
      },
      openRightPanelKeyboardShortcuts: (): void => {
        setState((s) => (s.rightPanelView === RightPanelView.KEYBOARD_SHORTCUTS ? s : {...s, rightPanelView: RightPanelView.KEYBOARD_SHORTCUTS}));
      },
      openRightPanelColorPalette: (): void => {
        setState((s) => (s.rightPanelView === RightPanelView.COLOR_PALETTE ? s : {...s, rightPanelView: RightPanelView.COLOR_PALETTE}));
      },
      openRightPanelColumnsAll: (): void => {
        setState((s) => (s.rightPanelView === RightPanelView.COLUMNS_ALL ? s : {...s, rightPanelView: RightPanelView.COLUMNS_ALL}));
      },
      openRightPanelColorPicker: (target: EmailMakerColorPickerTarget): void => {
        setState((s) => ({...s, rightPanelView: RightPanelView.COLOR_PICKER, colorPickerTarget: target}));
      },
      openRightPanelBrandKitSuggestions: (target: EmailMakerBrandKitSuggestionsTarget): void => {
        setState((s) => ({...s, rightPanelView: RightPanelView.BRAND_KIT_SUGGESTIONS, brandKitSuggestionsTarget: target}));
      },
      setColorPickerTarget: (target: EmailMakerColorPickerTarget | null): void => {
        setState((s) => (s.colorPickerTarget === target ? s : {...s, colorPickerTarget: target}));
      },
      openRightPanelVersionHistory: (): void => {
        setState((s) => (s.rightPanelView === RightPanelView.VERSION_HISTORY ? s : {...s, rightPanelView: RightPanelView.VERSION_HISTORY}));
      },
      closeRightPanelView: (): void => {
        setState((s) =>
          s.rightPanelView === RightPanelView.DEFAULT && s.colorPickerTarget === null && s.brandKitSuggestionsTarget === null
            ? s
            : {...s, rightPanelView: RightPanelView.DEFAULT, colorPickerTarget: null, brandKitSuggestionsTarget: null}
        );
      },
    }),
    [editor, save]
  );

  return useMemo(
    () => ({
      editor,
      store: args.store,
      state,
      actions,
    }),
    [editor, args.store, state, actions]
  );
}

function resolveEditorViewport(isMobile: boolean, isTablet: boolean): EditorViewport {
  if (isMobile) {
    return EditorViewport.MOBILE;
  }
  if (isTablet) {
    return EditorViewport.TABLET;
  }
  return EditorViewport.DESKTOP;
}

type SetStateFn = (updater: (s: EmailMakerState) => EmailMakerState) => void;

/** Dismisses the color-picker overlay (bound to one component) back to the sector list when the selection changes. */
function closePerComponentOverlay(state: EmailMakerState): EmailMakerState {
  if (state.rightPanelView !== RightPanelView.COLOR_PICKER) {
    return state;
  }
  return {...state, rightPanelView: RightPanelView.DEFAULT, colorPickerTarget: null};
}

interface CreatedEmailMakerEditor {
  editor: Editor;
  /** Null only if GrapesJS never ran the config plugin (it runs plugins synchronously inside init). */
  teardownPlugin: (() => void) | null;
  teardownNamespace: () => void;
}

/** Seeds the namespace and mounts the GrapesJS editor with the full email maker plugin. */
function createEmailMakerEditor(container: HTMLElement, store: EmailMakerImmutableStore): CreatedEmailMakerEditor {
  const teardownNamespace = seedEmailMakerNamespace();
  window.PMW.initTracking();

  let teardownPlugin: (() => void) | null = null;
  const editor = mountEditor({
    container,
    plugin: (editorInstance: Editor): void => {
      teardownPlugin = installEmailMakerPlugin(editorInstance, buildEmailMakerPluginDeps(editorInstance, store));
    },
  });
  return {editor, teardownPlugin, teardownNamespace};
}

function buildEmailMakerPluginDeps(editor: Editor, store: EmailMakerImmutableStore): EmailMakerPluginDeps {
  return {
    store,
    savePayloadSources: {
      getInlinedHtml,
    },
    componentsOptions: {
      isEditorAiContentGenerationEnabled: store.isEmailEditorAiTextGenerationEnabled,
      isAiEditEnabled: (): boolean => isAiEditEnabled(editor),
    },
  };
}

function wireEditorListeners(editor: Editor, setState: SetStateFn): void {
  editor.on('component:selected', (component: Component) => {
    setState((s) => (s.selectedComponent === component ? s : closePerComponentOverlay({...s, selectedComponent: component})));
  });
  editor.on('component:deselected', () => {
    setState((s) => (s.selectedComponent === null ? s : closePerComponentOverlay({...s, selectedComponent: null})));
  });
  editor.on('storage:start:store', () => {
    setState((s) => ({...s, saveState: EmailMakerSaveState.SAVING}));
  });
  // Skip queue-no-op stores so SAVING and any recorded save error survive until the real save settles.
  editor.on('storage:after:store', () => {
    const isSaveInFlight = isEmailMakerSaveInFlight(editor);
    const autosaveEnabled = isEmailMakerAutosaveEnabled(editor);
    setState((s) => (isSaveInFlight ? s : {...s, autosaveEnabled, saveError: null, saveState: EmailMakerSaveState.SAVED}));
  });
  editor.on('storage:error:store', (error: unknown) => {
    if (isAuthenticateUserSaveError(error)) {
      handleAnonymousSaveAuthPrompt(editor, setState);
      return;
    }
    const autosaveEnabled = isEmailMakerAutosaveEnabled(editor);
    const saveError = toSaveErrorState(error);
    if (saveError?.code === EmailMakerSaveErrorCode.INVALID_CAMPAIGN_URLS) {
      trackGA4CustomEvent(GA4EventName.EMAILMAKER_SAVE_BLOCKED_BY_BAD_LINK);
    }
    setState((s) => ({...s, autosaveEnabled, saveError, saveState: EmailMakerSaveState.NOT_SAVED}));
  });
  editor.on('storage:error:load', () => {
    setState((s) => ({...s, loadFailed: true}));
  });
  editor.on(EMAIL_MAKER_BANNER_EVENT, (banner: EmailMakerBannerOptions | null) => {
    setState((s) => (s.banner === banner ? s : {...s, banner}));
  });
}

/** The backend rejects an anonymous save with an empty-message `authenticate-user` code; it is a login prompt, not an error to surface. */
function isAuthenticateUserSaveError(error: unknown): boolean {
  return error instanceof EmailMakerSaveError && error.code === EmailMakerSaveErrorCode.AUTHENTICATE_USER;
}

/**
 * Anonymous save: open the auth modal and, once logged in, retry the save so the template persists.
 * The status stays NOT_SAVED with no error popup, so dismissing the modal leaves the Save button
 * usable and a second Save re-opens the modal.
 *
 * Desync guard: when the client still thinks it's logged in (server session expired mid-edit),
 * `authenticateUser` would skip the modal and immediately retry the same doomed save, looping
 * forever — so leave the silent-unsaved state instead of prompting.
 */
function handleAnonymousSaveAuthPrompt(editor: Editor, setState: SetStateFn): void {
  const autosaveEnabled = isEmailMakerAutosaveEnabled(editor);
  setState((s) => ({...s, autosaveEnabled, saveError: null, saveState: EmailMakerSaveState.NOT_SAVED}));
  if (isUserLoggedIn()) {
    return;
  }
  authenticateUser(editor, {
    cb: (): void => {
      void saveEmailTemplateToCache(editor);
    },
  });
}

function toSaveErrorState(error: unknown): EmailMakerSaveErrorState | null {
  if (error instanceof EmailMakerConcurrentEditError) {
    return {code: EmailMakerSaveErrorCode.EXISTING_SAVE_DATA, conflictPayload: error.payload};
  }
  if (error instanceof EmailMakerSaveError) {
    if (error.code === EmailMakerSaveErrorCode.INVALID_CAMPAIGN_URLS) {
      return {code: error.code, invalidUrls: error.invalidUrls};
    }
    const message = htmlToPlainText(error.message);
    if (message.length > 0) {
      return {code: error.code, message};
    }
  }
  return null;
}

/** Strips HTML tags from a backend message to plain text; never touches the live DOM, so the result is safe to render as text. */
function htmlToPlainText(html: string): string {
  return new DOMParser().parseFromString(html, 'text/html').body.textContent ?? '';
}

/**
 * Post-load bootstrap: clears undo history so user-facing undo never reverts the initial load.
 * isEditorLoading stays true on failure — GrapesJS fires `load` even after a fetch failure.
 */
function finishEditorLoad(editor: Editor, setState: SetStateFn): void {
  editor.UndoManager.clear();
  const autosaveEnabled = isEmailMakerAutosaveEnabled(editor);
  setState((s) => (s.isEditorLoading && !s.loadFailed ? {...s, autosaveEnabled, isEditorLoading: false} : s));
}

function exposeEditorGlobal(editor: Editor): void {
  window.pmwEmailMakerEditor = editor;
}

function clearEditorGlobal(): void {
  delete window.pmwEmailMakerEditor;
}
